/**
 * Product Media Slider Component
 * Handles swipeable product images on collection pages.
 * Extra slides are mounted, then Swiper starts, once the card is about a
 * screen away — one slider per turn so a grid never becomes one long task.
 */

import { Component } from "@theme/component-base";

const initQueue = [];
let initQueueRunning = false;
let nearViewportObserver = null;

const yieldToMain = () =>
  window.scheduler?.yield
    ? window.scheduler.yield()
    : new Promise((resolve) => setTimeout(resolve, 0));

async function drainInitQueue() {
  if (initQueueRunning) return;
  initQueueRunning = true;
  while (initQueue.length) {
    const slider = initQueue.shift();
    if (slider.isConnected) slider.startWhenReady();
    if (initQueue.length) await yieldToMain();
  }
  initQueueRunning = false;
}

export class ProductMediaSlider extends Component {
  connectedCallback() {
    if (this.#bound) return;
    this.#bound = true;
    this.#setupSwatchListener();
    this.#whenNearViewport();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    nearViewportObserver?.unobserve(this);
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  /**
   * Called from the init queue once this card is near the viewport.
   */
  startWhenReady() {
    if (this.swiper || this.#started) return;
    if (typeof window.Swiper === "undefined") {
      setTimeout(() => {
        initQueue.push(this);
        drainInitQueue();
      }, 100);
      return;
    }

    this.#started = true;
    this.#mountDeferredSlides();
    this.#initSwiper();
    this.setAttribute("data-slider-initialized", "");
    if (this.#pendingMediaId) {
      const mediaId = this.#pendingMediaId;
      this.#pendingMediaId = null;
      this.#onSwatchChange(mediaId);
    }
  }

  #bound = false;
  #started = false;
  #pendingMediaId = null;

  #whenNearViewport() {
    if (window.Shopify?.designMode || !("IntersectionObserver" in window)) {
      initQueue.push(this);
      drainInitQueue();
      return;
    }

    nearViewportObserver ??= new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          nearViewportObserver.unobserve(entry.target);
          initQueue.push(entry.target);
        });
        drainInitQueue();
      },
      { rootMargin: "100% 0px" }
    );
    nearViewportObserver.observe(this);
  }

  #mountDeferredSlides() {
    const dataEl = this.querySelector("[data-deferred-slides]");
    if (!dataEl) return;

    let slides = [];
    try {
      slides = JSON.parse(dataEl.textContent);
    } catch {
      dataEl.remove();
      return;
    }

    const wrapper = this.querySelector(".swiper-wrapper");
    const template = wrapper?.querySelector(".swiper-slide");
    if (!wrapper || !template || !Array.isArray(slides)) {
      dataEl.remove();
      return;
    }

    slides.forEach((slide) => {
      const clone = template.cloneNode(true);
      clone.dataset.mediaId = String(slide.id);
      clone.querySelectorAll("[data-product-image], img").forEach((img) => {
        if (!(img instanceof HTMLImageElement)) return;
        img.closest("picture")?.querySelectorAll("source").forEach((source) => {
          source.removeAttribute("data-srcset");
          if (slide.srcset) source.srcset = slide.srcset;
        });
        img.removeAttribute("data-src");
        img.removeAttribute("data-srcset");
        img.removeAttribute("data-lazy-image");
        img.removeAttribute("fetchpriority");
        img.loading = "lazy";
        img.alt = slide.alt || "";
        img.src = slide.src;
        if (slide.srcset) img.srcset = slide.srcset;
        img.classList.remove("opacity-0");
      });
      const hover = clone.querySelector(".md\\:group-hover\\:opacity-100");
      hover?.remove();
      wrapper.appendChild(clone);
    });

    dataEl.remove();
  }

  #setupSwatchListener() {
    const swatchComponent = this.closest("[data-product-card]")?.querySelector(
      "swatches-item-component"
    );

    if (swatchComponent) {
      swatchComponent.addEventListener("swatch:change", (e) => {
        this.#onSwatchChange(e.detail.mediaId);
      });
    }
  }

  #onSwatchChange(mediaId) {
    if (!mediaId) return;
    if (!this.swiper) {
      this.#pendingMediaId = mediaId;
      return;
    }

    const slides = this.querySelectorAll(".swiper-slide[data-media-id]");
    const slideIndex = Array.from(slides).findIndex(
      (slide) => slide.dataset.mediaId === String(mediaId)
    );

    if (slideIndex !== -1) {
      this.swiper.slideTo(slideIndex);
    }
  }

  #initSwiper() {
    const wrapper = this.querySelector(".swiper");
    if (!wrapper) return;

    const slides = wrapper.querySelectorAll(".swiper-slide");
    if (slides.length <= 1) return;

    try {
      this.swiper = new window.Swiper(wrapper, {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: false,
        navigation: {
          nextEl: wrapper.querySelector(".product-card-controls-left"),
          prevEl: wrapper.querySelector(".product-card-controls-right"),
        },
        touchStartPreventDefault: false,
        preventClicks: false,
        preventClicksPropagation: false,
      });
    } catch (error) {
      console.error("[ProductMediaSlider] Failed to initialize:", error);
    }
  }
}

customElements.define("product-media-slider", ProductMediaSlider);
